import React from "react";
import {
  TASK_PRIORITY_LABELS,
  TICKET_IMPACT_LABELS,
  TICKET_STATE_LABELS,
  TICKET_URGENCY_LABELS,
  TaskPriority,
  TicketImpact,
  TicketState,
  TicketUrgency,
} from "../../../../interfaces/Ticket";

/**
 * Badges de estado/prioridade/impacto/urgência do chamado — mesma convenção de
 * `projectTasks/components/TaskBoard.tsx` (mapa de cor `Record<number, string>` colado ao
 * componente que o usa, sempre com par claro/escuro).
 */

const pill = "rounded-full px-2.5 py-1 text-[11px] font-bold whitespace-nowrap";

const STATE_COLOR: Record<number, string> = {
  [TicketState.Aberto]: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  [TicketState.EmAtendimento]:
    "bg-dv-cyan-100 text-dv-cyan-700 dark:bg-dv-cyan-900/40 dark:text-dv-cyan-300",
  [TicketState.PendenteCliente]:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  [TicketState.Resolvido]:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  [TicketState.Fechado]: "bg-gray-200 text-gray-700 dark:bg-navy-900 dark:text-gray-300",
  [TicketState.Reaberto]: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

export const TicketStateBadge: React.FC<{ state: number; className?: string }> = ({
  state,
  className,
}) => (
  <span
    className={`${pill} ${STATE_COLOR[state] ?? "bg-gray-100 text-gray-700 dark:bg-navy-900 dark:text-gray-300"} ${className ?? ""}`}
  >
    {TICKET_STATE_LABELS[state] ?? `Estado ${state}`}
  </span>
);

const PRIORITY_COLOR: Record<number, string> = {
  [TaskPriority.Low]: "bg-gray-100 text-gray-700 dark:bg-navy-900 dark:text-gray-300",
  [TaskPriority.Medium]: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  [TaskPriority.High]: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  [TaskPriority.Urgent]: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

export const TicketPriorityBadge: React.FC<{ priority: number; className?: string }> = ({
  priority,
  className,
}) => (
  <span
    className={`${pill} ${PRIORITY_COLOR[priority] ?? "bg-gray-100 text-gray-700 dark:bg-navy-900 dark:text-gray-300"} ${className ?? ""}`}
  >
    {TASK_PRIORITY_LABELS[priority] ?? `Prioridade ${priority}`}
  </span>
);

const IMPACT_COLOR: Record<number, string> = {
  [TicketImpact.UmaPessoa]: "bg-gray-100 text-gray-700 dark:bg-navy-900 dark:text-gray-300",
  [TicketImpact.UmaArea]: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  [TicketImpact.Empresa]: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

export const TicketImpactBadge: React.FC<{ impact: number; className?: string }> = ({
  impact,
  className,
}) => (
  <span
    className={`${pill} ${IMPACT_COLOR[impact] ?? "bg-gray-100 text-gray-700 dark:bg-navy-900 dark:text-gray-300"} ${className ?? ""}`}
  >
    {TICKET_IMPACT_LABELS[impact] ?? `Impacto ${impact}`}
  </span>
);

const URGENCY_COLOR: Record<number, string> = {
  [TicketUrgency.PodeEsperar]: "bg-gray-100 text-gray-700 dark:bg-navy-900 dark:text-gray-300",
  [TicketUrgency.AtrapalhaHoje]: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  [TicketUrgency.ParadoAgora]: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

export const TicketUrgencyBadge: React.FC<{ urgency: number; className?: string }> = ({
  urgency,
  className,
}) => (
  <span
    className={`${pill} ${URGENCY_COLOR[urgency] ?? "bg-gray-100 text-gray-700 dark:bg-navy-900 dark:text-gray-300"} ${className ?? ""}`}
  >
    {TICKET_URGENCY_LABELS[urgency] ?? `Urgência ${urgency}`}
  </span>
);
